import { useAgencyFilter } from 'hooks/useAgencyFilter';
import { useLeadAgentFilter } from 'hooks/useLeadAgentFilter';
import {
  States,
  BlankContractStatuses,
  CampaignAdminModelMarketingStatus,
  CampaignsApiContext,
  CampaignStatuses,
  LaunchDateStatuses,
  MarketingStatuses,
  SortingOption,
} from '@urbanx/agx.campaigns.client';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { SelectChipOption, SortOrder } from '@urbanx/agx.ui-components2';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createAgencyLookup } from 'utils/agencyUtils';
import { splitPascalCase } from 'utils/stringUtils';
import { getStatusConfig } from '../components/campaignMarketing/CampaignFlowStatusBadge';
import {
  createInitialCampaignsDashboardFilterState,
  storeCampaignsDashboardFilterState,
} from './CampaignsDashboardFilterState';

const PAGE_SIZE = 20;

const campaignStatusOptions = Object.values(CampaignStatuses).map((status) => ({
  label: splitPascalCase(status).replace(/^(Agreement|Listing)\s+/, ''),
  value: status,
}));

const marketingStatusOptions = Object.values(MarketingStatuses).map((status) => ({
  label:
    getStatusConfig(status as unknown as CampaignAdminModelMarketingStatus)?.text ??
    splitPascalCase(status),
  value: status,
}));

const contractStatusOptions = Object.values(BlankContractStatuses).map((status) => ({
  label: splitPascalCase(status),
  value: status,
}));

const launchDateStatusOptions = [
  {
    label: 'This week',
    value: LaunchDateStatuses.ThisWeek,
  },
  {
    label: 'Next week',
    value: LaunchDateStatuses.NextWeek,
  },
  {
    label: 'Launch date not set',
    value: LaunchDateStatuses.LaunchDateNotSet,
  },
  {
    label: 'Off market',
    value: LaunchDateStatuses.OffMarket,
  },
];

const stateOptions = [
  { label: 'QLD', value: States.Qld },
  { label: 'NSW', value: States.Nsw },
];

const buildSelectedOptions = (
  options: { label: string; value: string }[],
  selectedValues?: string[]
) => {
  return options.map((option) => ({
    ...option,
    selected: selectedValues?.includes(option.value) ?? false,
  }));
};

const useCampaignsDashboard = () => {
  const api = useContext(CampaignsApiContext);
  const [initialFilterState] = useState(createInitialCampaignsDashboardFilterState);
  const [selectedAgencyValues, setSelectedAgencyValues] = useState<string[] | undefined>(
    initialFilterState.agencyIds
  );
  const [selectedLeadAgentIds, setSelectedLeadAgentIds] = useState<string[] | undefined>(
    initialFilterState.leadAgentIds
  );
  const [selectedStates, setSelectedStates] = useState<States[] | undefined>(
    initialFilterState.states
  );
  const [selectedCampaignStatuses, setSelectedCampaignStatuses] = useState<
    CampaignStatuses[] | undefined
  >(initialFilterState.campaignStatuses);
  const [selectedBlankContractStatuses, setSelectedBlankContractStatuses] = useState<
    BlankContractStatuses[] | undefined
  >(initialFilterState.blankContractStatuses);
  const [selectedLaunchDateStatuses, setSelectedLaunchDateStatuses] = useState<
    LaunchDateStatuses[] | undefined
  >(initialFilterState.launchDateStatuses);
  const [selectedMarketingStatuses, setSelectedMarketingStatuses] = useState<
    MarketingStatuses[] | undefined
  >(initialFilterState.marketingStatuses);
  const [selectedSortingOption, setSelectedSortingOption] = useState<SortingOption | undefined>(
    SortingOption.LaunchDateAsc
  );
  const [addressSearchText, setAddressSearchText] = useState('');
  const [lastRowElement, setLastRowElement] = useState<HTMLDivElement | null>(null);
  const [isLastRowVisible, setIsLastRowVisible] = useState(false);
  const {
    agencies,
    agencyOptions: agencyFilterOptions,
    effectiveAgencyIds,
    isPending: isAgencyFilterPending,
    isAgencyScopeUnavailable,
    hasNoMatchingAgencies,
  } = useAgencyFilter({ selectedValues: selectedAgencyValues });
  const dashboardEnabled =
    !isAgencyFilterPending && !isAgencyScopeUnavailable && !hasNoMatchingAgencies;
  const hasSelectedAgencies = !!selectedAgencyValues?.length;
  const { leadAgentFilterOptions, agentLookup } = useLeadAgentFilter({
    effectiveAgencyIds,
    selectedLeadAgentIds,
  });

  const { data, isPending, isFetching, isError, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [
      'staffGetCampaignDashboard',
      effectiveAgencyIds,
      selectedLeadAgentIds,
      selectedStates,
      selectedCampaignStatuses,
      selectedLaunchDateStatuses,
      selectedMarketingStatuses,
      selectedBlankContractStatuses,
      addressSearchText || undefined,
      PAGE_SIZE,
      selectedSortingOption,
    ],
    initialPageParam: undefined as string | undefined,
    enabled: dashboardEnabled && !!api,
    queryFn: ({ pageParam, signal }) =>
      api.staffGetCampaignDashboard(
        effectiveAgencyIds,
        selectedLeadAgentIds,
        selectedStates,
        selectedCampaignStatuses,
        selectedLaunchDateStatuses,
        selectedMarketingStatuses,
        selectedBlankContractStatuses,
        addressSearchText || undefined,
        pageParam,
        PAGE_SIZE,
        selectedSortingOption,
        signal
      ),
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.continuationToken && lastPage.continuationToken !== lastPageParam
        ? lastPage.continuationToken
        : undefined,
  });

  const rows = dashboardEnabled
    ? (data?.pages.flatMap((page) => page.results ?? []) ?? []).filter(
        (row, index, allRows) =>
          allRows.findIndex((item) => item.campaignId === row.campaignId) === index
      )
    : [];

  useEffect(() => {
    storeCampaignsDashboardFilterState({
      states: selectedStates,
      agencyIds: selectedAgencyValues,
      leadAgentIds: selectedLeadAgentIds,
      campaignStatuses: selectedCampaignStatuses,
      blankContractStatuses: selectedBlankContractStatuses,
      launchDateStatuses: selectedLaunchDateStatuses,
      marketingStatuses: selectedMarketingStatuses,
    });
  }, [
    selectedAgencyValues,
    selectedLeadAgentIds,
    selectedStates,
    selectedBlankContractStatuses,
    selectedCampaignStatuses,
    selectedLaunchDateStatuses,
    selectedMarketingStatuses,
  ]);

  const lastRowRef = useCallback((node: HTMLDivElement | null) => {
    setLastRowElement(node);
  }, []);

  useEffect(() => {
    if (!lastRowElement) {
      setIsLastRowVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsLastRowVisible(entries[0].isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px 0px 300px 0px',
        threshold: 0,
      }
    );

    observer.observe(lastRowElement);

    return () => observer.disconnect();
  }, [lastRowElement]);

  useEffect(() => {
    if (!isLastRowVisible || !hasNextPage || isFetching || isError) return;

    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage, hasNextPage, isError, isFetching, isLastRowVisible]);

  const agencyLookup = useMemo(() => createAgencyLookup(agencies), [agencies]);

  const onSortLaunch = useCallback((sortOrder: SortOrder) => {
    const nextSortingOption =
      sortOrder === 1
        ? SortingOption.LaunchDateAsc
        : sortOrder === -1
          ? SortingOption.LaunchDateDesc
          : undefined;

    setSelectedSortingOption(nextSortingOption);
  }, []);

  const selectedCampaignStatusOptions = useMemo(() => {
    return buildSelectedOptions(campaignStatusOptions, selectedCampaignStatuses);
  }, [selectedCampaignStatuses]);

  const selectedStateOptions = useMemo(() => {
    return buildSelectedOptions(stateOptions, selectedStates);
  }, [selectedStates]);

  const selectedContractStatusOptions = useMemo(() => {
    return buildSelectedOptions(contractStatusOptions, selectedBlankContractStatuses);
  }, [selectedBlankContractStatuses]);

  const selectedLaunchDateStatusOptions = useMemo(() => {
    return buildSelectedOptions(launchDateStatusOptions, selectedLaunchDateStatuses);
  }, [selectedLaunchDateStatuses]);

  const selectedMarketingStatusOptions = useMemo(() => {
    return buildSelectedOptions(marketingStatusOptions, selectedMarketingStatuses);
  }, [selectedMarketingStatuses]);

  const handleAgencyFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedAgencyValues(options.length > 0 ? options.map((option) => option.value) : undefined);
    setSelectedLeadAgentIds(undefined);
  }, []);

  const handleLeadAgentFilterSelect = (options: SelectChipOption[]) => {
    setSelectedLeadAgentIds(options.length > 0 ? options.map((option) => option.value) : undefined);
  };

  const handleStateFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedStates(
      options.length > 0 ? options.map((option) => option.value as States) : undefined
    );
  }, []);

  const handleCampaignStatusFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedCampaignStatuses(
      options.length > 0 ? options.map((option) => option.value as CampaignStatuses) : undefined
    );
  }, []);

  const handleContractFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedBlankContractStatuses(
      options.length > 0
        ? options.map((option) => option.value as BlankContractStatuses)
        : undefined
    );
  }, []);

  const handleLaunchDateFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedLaunchDateStatuses(
      options.length > 0 ? options.map((option) => option.value as LaunchDateStatuses) : undefined
    );
  }, []);

  const handleMarketingFilterSelect = useCallback((options: SelectChipOption[]) => {
    setSelectedMarketingStatuses(
      options.length > 0 ? options.map((option) => option.value as MarketingStatuses) : undefined
    );
  }, []);

  return {
    rows,
    agencyLookup,
    agentLookup,
    isPending: isAgencyFilterPending || (dashboardEnabled && isPending),
    isAgenciesPending: isAgencyFilterPending,
    lastRowRef,
    addressSearchText,
    agencyFilterOptions,
    leadAgentFilterOptions,
    hasSelectedAgencies,
    selectedStateOptions,
    selectedCampaignStatusOptions,
    selectedContractStatusOptions,
    selectedLaunchDateStatusOptions,
    selectedMarketingStatusOptions,
    setAddressSearchText,
    handleStateFilterSelect,
    handleAgencyFilterSelect,
    handleLeadAgentFilterSelect,
    handleCampaignStatusFilterSelect,
    handleContractFilterSelect,
    handleLaunchDateFilterSelect,
    handleMarketingFilterSelect,
    onSortLaunch,
    resetStateFilters: () => setSelectedStates(undefined),
    resetAgencyFilters: () => {
      setSelectedAgencyValues(undefined);
      setSelectedLeadAgentIds(undefined);
    },
    resetLeadAgentFilters: () => setSelectedLeadAgentIds(undefined),
    resetCampaignStatusFilters: () => setSelectedCampaignStatuses(undefined),
    resetContractFilters: () => setSelectedBlankContractStatuses(undefined),
    resetLaunchDateFilters: () => setSelectedLaunchDateStatuses(undefined),
    resetMarketingFilters: () => setSelectedMarketingStatuses(undefined),
  };
};

export default useCampaignsDashboard;
